import { NextActions } from "@/components/operations/NextActions";
import { SourceDealActions } from "@/components/operations/SourceDealActions";
import { SuccessActionPanel } from "@/components/operations/SuccessActionPanel";
import { SuggestionApprovalPreview } from "@/components/operations/SuggestionApprovalPreview";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  useApproveOrderCandidate,
  useInventories,
  useOrderCandidateById,
  usePurchaseOrderById,
  useRejectOrderCandidate,
  useSalesOrderById,
  useUpdateOrderCandidate,
  useUsers,
} from "@/hooks";
import { useSourceDeals } from "@/hooks/service-hooks/deals/use-won-deals";
import { useProductsStore } from "@/stores/products";
import { formatTimestamp } from "@/utils/formatting";
import { getHubspotDealHref } from "@/utils/get-hubspot-deal-href";
import { useEffect, useMemo, useState } from "react";
import {
  buildInventoryWorkspacePath,
  formatStatusLabel,
  getInventoryOperationalStatus,
  isInventoryBlockedForSalesOrderLinking,
} from "./operations-shared";

interface OrderSuggestionSheetProps {
  candidateId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ActionFeedbackState {
  type: "success" | "error";
  title: string;
  description: string;
  details?: string[];
  nextAction?: string;
  actions?: Array<{
    label: string;
    href?: string;
    onClick?: () => void;
  }>;
}

function abbreviateProductName(name: string): string {
  const airlinePart = name.split("/")[0].trim();
  return airlinePart
    .split(/\s+/)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

function getErrorMessage(payload: {
  error: unknown;
  fallback: string;
}): string {
  const { error, fallback } = payload;
  if (typeof error !== "object" || error === null) {
    return fallback;
  }

  const record = error as Record<string, unknown>;
  if (typeof record.message === "string" && record.message.length > 0) {
    return record.message;
  }

  return fallback;
}

export function OrderSuggestionSheet(
  props: Readonly<OrderSuggestionSheetProps>,
) {
  const { candidateId, open, onOpenChange } = props;
  const candidateQuery = useOrderCandidateById(candidateId);
  const approveMutation = useApproveOrderCandidate();
  const rejectMutation = useRejectOrderCandidate();
  const updateMutation = useUpdateOrderCandidate();
  const usersQuery = useUsers();
  const inventoriesQuery = useInventories({
    pagination: { limit: 200 },
    orderBy: { field: "updatedAt", direction: "desc" },
  });
  const candidate = candidateQuery.data;
  const users = usersQuery.data ?? [];
  const inventories = useMemo(
    () => inventoriesQuery.data?.pages.flat() ?? [],
    [inventoriesQuery.data],
  );
  const products = useProductsStore((state) => state.products);
  const productCodeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const product of products) {
      if (product.id && product.productCode) {
        map.set(product.id, product.productCode);
      }
    }
    return map;
  }, [products]);
  const [flightDate, setFlightDate] = useState("");
  const [soldByUserId, setSoldByUserId] = useState("");
  const [bookingEndDate, setBookingEndDate] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editQuantity, setEditQuantity] = useState("");
  const [selectedInventoryId, setSelectedInventoryId] = useState("");
  const [feedbackState, setFeedbackState] =
    useState<ActionFeedbackState | null>(null);

  const hasCandidate = Boolean(candidate);
  const loadedCandidateId = candidate?.id;
  const candidateAmount = candidate?.amount;
  const candidateQuantity = candidate?.quantity;

  useEffect(() => {
    if (hasCandidate) {
      setEditAmount(String(candidateAmount ?? ""));
      setEditQuantity(String(candidateQuantity ?? ""));
    }
    setSelectedInventoryId("");
  }, [hasCandidate, loadedCandidateId, candidateAmount, candidateQuantity]);

  const isEditable = Boolean(
    candidate && ["suggested", "approved"].includes(candidate.status),
  );
  const editPreviewUnitPrice = (() => {
    const a = Number(editAmount);
    const q = Number(editQuantity);
    if (!Number.isFinite(a) || !Number.isFinite(q) || q <= 0) {
      return null;
    }
    return a / q;
  })();
  const editDirty = Boolean(
    candidate &&
      (Number(editAmount) !== candidate.amount ||
        Number(editQuantity) !== candidate.quantity),
  );

  const inventorySufficiency = useMemo(() => {
    if (!candidate || candidate.candidateType !== "so") {
      return undefined;
    }

    const eligibleInventories = inventories.filter(
      (inventory) =>
        inventory.productId === candidate.productId &&
        inventory.availableMiles > 0 &&
        !["restricted", "closed"].includes(inventory.accountStatus) &&
        !isInventoryBlockedForSalesOrderLinking({ inventory }) &&
        getInventoryOperationalStatus({ inventory }) !== "closed",
    );
    const totalAvailableMiles = eligibleInventories.reduce((sum, inventory) => {
      return sum + inventory.availableMiles;
    }, 0);
    const maxSingleInventoryMiles = eligibleInventories.reduce(
      (max, inventory) => {
        return Math.max(max, inventory.availableMiles);
      },
      0,
    );

    return {
      allocationPossible: maxSingleInventoryMiles >= candidate.quantity,
      availableMiles: totalAvailableMiles,
      shortfallMiles: Math.max(0, candidate.quantity - maxSingleInventoryMiles),
      inventoryCount: eligibleInventories.length,
      maxSingleInventoryMiles,
    };
  }, [candidate, inventories]);

  // Inventories for the same product that the approved order can be linked to.
  // POs can top up any non-closed inventory; SOs need a single inventory with
  // enough available miles (and not blocked) to allocate from.
  const inventoryOptions = useMemo(() => {
    if (!candidate) {
      return [];
    }

    const sameProduct = inventories.filter(
      (inventory) => inventory.productId === candidate.productId,
    );

    if (candidate.candidateType === "so") {
      return sameProduct.filter(
        (inventory) =>
          inventory.availableMiles >= candidate.quantity &&
          !["restricted", "closed"].includes(inventory.accountStatus) &&
          !isInventoryBlockedForSalesOrderLinking({ inventory }) &&
          getInventoryOperationalStatus({ inventory }) !== "closed",
      );
    }

    return sameProduct.filter(
      (inventory) =>
        inventory.status !== "archived" &&
        getInventoryOperationalStatus({ inventory }) !== "closed",
    );
  }, [candidate, inventories]);

  const purchaseOrderQuery = usePurchaseOrderById(
    candidate?.candidateType === "po" ? candidate.convertedOrderId || "" : "",
  );
  const salesOrderQuery = useSalesOrderById(
    candidate?.candidateType === "so" ? candidate.convertedOrderId || "" : "",
  );
  const sourceDealsQuery = useSourceDeals(
    candidate?.sourceDealId ? [candidate.sourceDealId] : [],
  );
  const sourceDeal = sourceDealsQuery.data?.[0];
  const crmHref = getHubspotDealHref({
    dealId: candidate?.sourceDealId,
    hubspotLink: sourceDeal?.hubspotLink,
  });

  const createdRecordLinks = useMemo(() => {
    if (!candidate?.convertedOrderId) {
      return [];
    }

    if (candidate.candidateType === "po") {
      const purchaseOrder = purchaseOrderQuery.data;
      return [
        {
          label: "Open Purchase Order",
          href: `/operations/purchase-orders?poId=${candidate.convertedOrderId}`,
        },
        purchaseOrder?.inventoryId
          ? {
              label: "Open Inventory Workspace",
              href: buildInventoryWorkspacePath({
                inventoryId: purchaseOrder.inventoryId,
                purchaseOrderId: purchaseOrder.id,
                focus: "purchase-orders",
              }),
            }
          : null,
        purchaseOrder?.inventoryId
          ? {
              label: "Open Inventory",
              href: `/operations/inventory?inventoryId=${purchaseOrder.inventoryId}`,
            }
          : null,
      ].filter(Boolean) as Array<{ label: string; href: string }>;
    }

    const salesOrder = salesOrderQuery.data;
    return [
      {
        label: "Open Sales Order",
        href: `/operations/sales-orders?soId=${candidate.convertedOrderId}`,
      },
      salesOrder?.inventoryId
        ? {
            label: "Open Inventory Workspace",
            href: buildInventoryWorkspacePath({
              inventoryId: salesOrder.inventoryId,
              salesOrderId: salesOrder.id,
              focus: "sell-orders",
            }),
          }
        : null,
    ].filter(Boolean) as Array<{ label: string; href: string }>;
  }, [candidate, purchaseOrderQuery.data, salesOrderQuery.data]);

  const recordHref = candidate?.convertedOrderId
    ? candidate.candidateType === "po"
      ? `/operations/purchase-orders?poId=${candidate.convertedOrderId}`
      : `/operations/sales-orders?soId=${candidate.convertedOrderId}`
    : undefined;

  const workspaceHref =
    candidate?.candidateType === "po"
      ? purchaseOrderQuery.data?.inventoryId
        ? buildInventoryWorkspacePath({
            inventoryId: purchaseOrderQuery.data.inventoryId,
            purchaseOrderId: purchaseOrderQuery.data.id,
            focus: "purchase-orders",
          })
        : undefined
      : candidate?.candidateType === "so"
        ? salesOrderQuery.data?.inventoryId
          ? buildInventoryWorkspacePath({
              inventoryId: salesOrderQuery.data.inventoryId,
              salesOrderId: salesOrderQuery.data.id,
              focus: "sell-orders",
            })
          : undefined
        : undefined;

  const handleApprove = async (options?: { inventoryId?: string }) => {
    if (!candidate) {
      return;
    }

    const targetInventoryId = options?.inventoryId;

    if (candidate.candidateType === "so") {
      if (!flightDate) {
        setFeedbackState({
          type: "error",
          title: "Missing flight date",
          description:
            "Flight date is required before approving a sales order suggestion.",
        });
        return;
      }

      if (!soldByUserId) {
        setFeedbackState({
          type: "error",
          title: "Missing sold-by user",
          description:
            "Choose the user who made the sale before approving this suggestion.",
        });
        return;
      }

      if (!bookingEndDate) {
        setFeedbackState({
          type: "error",
          title: "Missing booking end date",
          description:
            "Booking end date is required so the allocated lots can be released later.",
        });
        return;
      }
    }

    try {
      const approvalPayload =
        candidate.candidateType === "so"
          ? {
              candidateId,
              override: {
                flightDate,
                soldByUserId,
                bookingEndDate,
              },
              ...(targetInventoryId
                ? { inventoryId: targetInventoryId }
                : {}),
            }
          : {
              candidateId,
              ...(targetInventoryId
                ? { inventoryId: targetInventoryId }
                : {}),
            };

      const result = await approveMutation.mutateAsync(approvalPayload);

      setFeedbackState({
        type: "success",
        title:
          result.candidateType === "po"
            ? "Purchase Order created"
            : "Sales Order created",
        description:
          result.candidateType === "po"
            ? "Approval created the purchase order and the linked inventory records."
            : "Approval created the sales order inside the selected inventory workspace.",
        details:
          result.candidateType === "po"
            ? [
                "Inventory was created or linked.",
                "Inventory credentials were initialized.",
              ]
            : [
                "Inventory totals were updated.",
                "The sales order was linked to the resolved inventory.",
              ],
        nextAction:
          result.candidateType === "po"
            ? "Review payment readiness and complete any missing account details."
            : "Review receivables and create an invoice if the order is finance-ready.",
        actions:
          result.candidateType === "po"
            ? ([
                {
                  label: "Open PO",
                  href: `/operations/purchase-orders?poId=${result.convertedOrderId}`,
                },
                result.inventoryId
                  ? {
                      label: "Open Inventory Workspace",
                      href: buildInventoryWorkspacePath({
                        inventoryId: result.inventoryId,
                        purchaseOrderId: result.convertedOrderId,
                        focus: "purchase-orders",
                      }),
                    }
                  : null,
                {
                  label: "Record Payment",
                  href: `/operations/finance?tab=payables&poId=${result.convertedOrderId}`,
                },
              ].filter(Boolean) as ActionFeedbackState["actions"])
            : ([
                {
                  label: "Open SO",
                  href: `/operations/sales-orders?soId=${result.convertedOrderId}`,
                },
                result.inventoryId
                  ? {
                      label: "Open Inventory Workspace",
                      href: buildInventoryWorkspacePath({
                        inventoryId: result.inventoryId,
                        salesOrderId: result.convertedOrderId,
                        focus: "sell-orders",
                      }),
                    }
                  : null,
                {
                  label: "Create Invoice",
                  href: `/operations/finance?tab=receivables&soId=${result.convertedOrderId}`,
                },
              ].filter(Boolean) as ActionFeedbackState["actions"]),
      });
    } catch (error) {
      setFeedbackState({
        type: "error",
        title: "Unable to approve suggestion",
        description: getErrorMessage({
          error,
          fallback: "Approval failed. Review the warning states and try again.",
        }),
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!candidate) {
      return;
    }
    const parsedAmount = editAmount === "" ? undefined : Number(editAmount);
    const parsedQuantity =
      editQuantity === "" ? undefined : Number(editQuantity);
    if (
      (parsedAmount !== undefined &&
        (!Number.isFinite(parsedAmount) || parsedAmount < 0)) ||
      (parsedQuantity !== undefined &&
        (!Number.isFinite(parsedQuantity) || parsedQuantity < 0))
    ) {
      setFeedbackState({
        type: "error",
        title: "Invalid value",
        description: "Amount and quantity must be non-negative numbers.",
      });
      return;
    }
    if (parsedAmount === undefined && parsedQuantity === undefined) {
      return;
    }
    const amountChanged =
      parsedAmount !== undefined && parsedAmount !== candidate.amount;
    const quantityChanged =
      parsedQuantity !== undefined && parsedQuantity !== candidate.quantity;
    if (!amountChanged && !quantityChanged) {
      return;
    }
    try {
      await updateMutation.mutateAsync({
        candidateId,
        data: {
          ...(amountChanged && { amount: parsedAmount }),
          ...(quantityChanged && { quantity: parsedQuantity }),
        },
      });
      setFeedbackState({
        type: "success",
        title: "Suggestion updated",
        description: "Amount and quantity were saved.",
      });
    } catch (error) {
      setFeedbackState({
        type: "error",
        title: "Unable to update suggestion",
        description: getErrorMessage({
          error,
          fallback: "Saving the suggestion failed. Please try again.",
        }),
      });
    }
  };

  const handleReject = async () => {
    try {
      await rejectMutation.mutateAsync({
        candidateId,
        reason: "Rejected from order suggestion review sheet",
      });

      setFeedbackState({
        type: "success",
        title: "Suggestion rejected",
        description:
          "The suggestion was rejected and will no longer be available for approval.",
        nextAction:
          "Review the source deal if a replacement suggestion is still needed.",
      });
    } catch (error) {
      setFeedbackState({
        type: "error",
        title: "Unable to reject suggestion",
        description: getErrorMessage({
          error,
          fallback: "Rejecting the suggestion failed. Please try again.",
        }),
      });
    }
  };

  const actions = candidate
    ? candidate.candidateType === "po"
      ? [
          {
            label: "Approve",
            description:
              "Create the purchase order and initialize the linked inventory.",
            onClick: () => void handleApprove(),
            variant: "default" as const,
            disabled:
              approveMutation.isPending ||
              !["suggested", "approved"].includes(candidate.status),
            loading: approveMutation.isPending,
            loadingText: "Approving...",
          },
          {
            label: "Reject",
            description:
              "Stop the conversion and keep the suggestion out of the active queue.",
            onClick: () => void handleReject(),
            variant: "outline" as const,
            disabled:
              rejectMutation.isPending ||
              !["suggested", "approved"].includes(candidate.status),
            loading: rejectMutation.isPending,
            loadingText: "Rejecting...",
          },
          ...(workspaceHref || recordHref
            ? [
                {
                  label: workspaceHref
                    ? "Open Inventory Workspace"
                    : "Open Purchase Order",
                  description: workspaceHref
                    ? "Review the purchase-side inventory workflow and linked financials."
                    : "Review the converted purchase order details.",
                  href: workspaceHref || recordHref,
                  variant: "outline" as const,
                },
              ]
            : []),
        ]
      : [
          {
            label: "Approve",
            description:
              "Create the sales order inside the linked inventory workspace.",
            onClick: () => void handleApprove(),
            variant: "default" as const,
            disabled:
              approveMutation.isPending ||
              !["suggested", "approved"].includes(candidate.status),
            loading: approveMutation.isPending,
            loadingText: "Approving...",
          },
          {
            label: "Reject",
            description:
              "Stop conversion and keep the suggestion from creating demand records.",
            onClick: () => void handleReject(),
            variant: "outline" as const,
            disabled:
              rejectMutation.isPending ||
              !["suggested", "approved"].includes(candidate.status),
            loading: rejectMutation.isPending,
            loadingText: "Rejecting...",
          },
          ...(workspaceHref || recordHref
            ? [
                {
                  label: workspaceHref
                    ? "Open Inventory Workspace"
                    : "Open Sales Order",
                  description: workspaceHref
                    ? "Review the sell-side inventory workflow, invoicing, and payments."
                    : "Review the converted sales order details.",
                  href: workspaceHref || recordHref,
                  variant: "outline" as const,
                },
              ]
            : []),
        ]
    : [];

  const headerActionsDisabled =
    !candidate || !["suggested", "approved"].includes(candidate.status);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-full overflow-y-auto p-0 md:max-w-3xl"
      >
        <div className="sticky top-0 z-20 border-b bg-background/95 px-6 py-5 backdrop-blur">
          <SheetHeader className="text-left">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-2">
                <SheetTitle>Suggestion Review</SheetTitle>
                <SheetDescription>
                  Review the downstream effects before approving or rejecting
                  this suggestion.
                </SheetDescription>
                {candidate ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">
                      {formatStatusLabel(candidate.candidateType)}
                    </Badge>
                    <Badge>{formatStatusLabel(candidate.status)}</Badge>
                    <span className="text-xs text-muted-foreground">
                      Updated{" "}
                      {formatTimestamp(
                        candidate.updatedAt,
                        "yyyy-MM-dd HH:mm",
                      ) || "-"}
                    </span>
                  </div>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => void handleApprove()}
                  disabled={headerActionsDisabled}
                  loading={approveMutation.isPending}
                  loadingText="Approving..."
                >
                  Approve
                </Button>
                <Button
                  variant="outline"
                  onClick={() => void handleReject()}
                  disabled={headerActionsDisabled}
                  loading={rejectMutation.isPending}
                  loadingText="Rejecting..."
                >
                  Reject
                </Button>
              </div>
            </div>
          </SheetHeader>
        </div>
        {candidate?.candidateType === "so" &&
        ["suggested", "approved"].includes(candidate.status) ? (
          <div className="border-b px-6 py-4">
            <div className="grid gap-3 md:grid-cols-3">
              <Input
                type="date"
                value={flightDate}
                onChange={(e) => setFlightDate(e.target.value)}
              />
              <SearchableSelect
                value={soldByUserId}
                onChange={setSoldByUserId}
                placeholder="Select user"
                searchPlaceholder="Search user..."
                emptyText="No user found."
                options={users.map((user) => ({
                  value: user.id,
                  label: user.email,
                  keywords: [user.email],
                }))}
              />
              <Input
                type="date"
                value={bookingEndDate}
                onChange={(e) => setBookingEndDate(e.target.value)}
              />
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Booking end date is when the linked inventory can be released from
              booking exposure.
            </div>
          </div>
        ) : null}

        {candidate &&
        ["suggested", "approved"].includes(candidate.status) ? (
          <div className="border-b px-6 py-4">
            <div className="text-sm font-medium">
              Add to inventory (optional)
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {candidate.candidateType === "po"
                ? "Pick an existing inventory for this product to add the purchased miles to. Leave empty to create a new inventory automatically."
                : "Pick an existing inventory for this product to allocate from. Leave empty to auto-select via FIFO."}
            </div>
            <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center">
              <div className="md:flex-1">
                <SearchableSelect
                  value={selectedInventoryId}
                  onChange={setSelectedInventoryId}
                  placeholder="Select inventory"
                  searchPlaceholder="Search inventory..."
                  emptyText={
                    inventoryOptions.length === 0
                      ? "No eligible inventory for this product."
                      : "No inventory found."
                  }
                  clearable
                  options={inventoryOptions.map((inventory) => {
                    const status = formatStatusLabel(
                      getInventoryOperationalStatus({ inventory }),
                    );
                    const productCode =
                      productCodeById.get(inventory.productId) ||
                      abbreviateProductName(inventory.productName);
                    const username = inventory.username || "-";
                    const owner = inventory.accountOwnerName || "-";
                    return {
                      value: inventory.id,
                      label: `${status} · ${productCode} · ${username} · ${owner} · ${inventory.availableMiles.toLocaleString()} mi`,
                      keywords: [
                        status,
                        productCode,
                        username,
                        owner,
                        inventory.name,
                        inventory.productName,
                      ],
                    };
                  })}
                />
              </div>
              <Button
                onClick={() =>
                  void handleApprove({ inventoryId: selectedInventoryId })
                }
                disabled={
                  !selectedInventoryId ||
                  approveMutation.isPending ||
                  !["suggested", "approved"].includes(candidate.status)
                }
                loading={approveMutation.isPending}
                loadingText="Approving..."
              >
                Approve &amp; add to inventory
              </Button>
            </div>
          </div>
        ) : null}

        <div className="space-y-6 px-6 py-6">
          {candidate && isEditable ? (
            <div className="rounded-xl border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">Edit amount &amp; quantity</div>
                  <div className="text-xs text-muted-foreground">
                    Correct the suggestion before approval. Unit price is
                    recomputed automatically.
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => void handleSaveEdit()}
                  disabled={!editDirty}
                  loading={updateMutation.isPending}
                  loadingText="Saving..."
                >
                  Save
                </Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Amount
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">
                    {candidate.candidateType === "so" ? "Miles" : "Quantity"}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step="1"
                    value={editQuantity}
                    onChange={(e) => setEditQuantity(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Unit Price (preview)
                  </label>
                  <Input
                    type="text"
                    value={
                      editPreviewUnitPrice !== null
                        ? editPreviewUnitPrice.toFixed(4)
                        : "-"
                    }
                    readOnly
                    disabled
                  />
                </div>
              </div>
            </div>
          ) : null}
          {candidate?.sourceDealId ? (
            <div className="rounded-xl border p-4 space-y-2">
              <div className="font-medium">Source Deal</div>
              <SourceDealActions sourceDealId={candidate.sourceDealId} />
            </div>
          ) : null}
          {feedbackState ? (
            feedbackState.type === "success" ? (
              <SuccessActionPanel
                title={feedbackState.title}
                description={feedbackState.description}
                details={feedbackState.details}
                nextAction={feedbackState.nextAction}
                actions={feedbackState.actions}
                onDismiss={() => setFeedbackState(null)}
                onNavigate={() => onOpenChange(false)}
              />
            ) : (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <div className="font-medium">{feedbackState.title}</div>
                <div className="mt-1">{feedbackState.description}</div>
              </div>
            )
          ) : null}

          {!candidate ? (
            <div className="text-sm text-muted-foreground">
              {candidateQuery.isLoading
                ? "Loading review..."
                : "Suggestion not found."}
            </div>
          ) : (
            <>
              <SuggestionApprovalPreview
                candidate={candidate}
                workspaceHref={workspaceHref}
                recordHref={recordHref}
                inventorySufficiency={inventorySufficiency}
                createdRecordLinks={createdRecordLinks}
                crmHref={crmHref}
                onNavigate={() => onOpenChange(false)}
              />

              <NextActions
                actions={actions}
                onNavigate={() => onOpenChange(false)}
              />

              <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
                <Button
                  onClick={() => void handleApprove()}
                  disabled={headerActionsDisabled}
                  loading={approveMutation.isPending}
                  loadingText="Approving..."
                >
                  Approve
                </Button>
                <Button
                  variant="outline"
                  onClick={() => void handleReject()}
                  disabled={headerActionsDisabled}
                  loading={rejectMutation.isPending}
                  loadingText="Rejecting..."
                >
                  Reject
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
